import { Seo } from "../components/Seo";
import { useGoToSection } from "../lib/useGo";
import { COMPARE } from "../components/data/content";
import Reveal from "../components/Reveal";
import Footer from "../components/Footer";
import StickyCTA from "../components/StickyCTA";

const FranchiseAlternative = () => {
  const go = useGoToSection();
  return (
    <>
      <Seo
        title="Real Estate Franchise Alternative | Agency HQ"
        description="Considering a franchise? Agency HQ is the alternative for experienced agents — your own brand, retain up to 95% commission, no establishment costs and full operational support."
        path="/real-estate-franchise-alternative"
      />
      <main className="pt-[72px]">
        <section className="relative overflow-hidden py-14 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-amber-600/15 blur-[140px]" aria-hidden="true" />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <p className="label-mono">Real estate franchise alternative</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="franchise-h1">
              The real estate franchise alternative for experienced agents.
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-400 sm:text-lg">
              A traditional franchise charges establishment fees, takes a share of every sale and locks
              your business to someone else&rsquo;s brand — while you still run the back office yourself.
              Going fully independent hands you the entire agency problem.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Agency HQ is the third option: a central operating platform that helps agents establish,
              manage and grow their own businesses — under their own brand, co-branded with Agency HQ or
              under the Agency HQ name — while retaining up to 95% of the commission they generate.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href="/#contact" onClick={go("contact", "seo-franchise")} data-testid="franchise-primary-cta-btn" className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-all duration-200 hover:from-amber-600 hover:to-amber-700">
                Book a confidential conversation
              </a>
              <a href="/#calculator" onClick={go("calculator", "seo-franchise")} data-testid="franchise-secondary-cta-btn" className="inline-flex items-center justify-center rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-gold/50 hover:text-gold-light">
                See what you could earn
              </a>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-surface/50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Franchise vs independent vs Agency HQ.
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-8 overflow-x-auto" data-testid="franchise-comparison">
                <div className="min-w-[720px]">
                  <div className="grid grid-cols-[1.1fr_1fr_1fr_1.15fr]">
                    <div />
                    {COMPARE.columns.map((c, i) => (
                      <div key={c} className={`rounded-t-2xl border px-5 py-4 ${i === 2 ? "border-gold/50 bg-gold/10" : "border-white/10 bg-panel/40"}`}>
                        <p className={`font-display text-sm font-bold ${i === 2 ? "text-gold-light" : "text-slate-300"}`}>{c}</p>
                      </div>
                    ))}
                  </div>
                  {COMPARE.rows.map((row, r) => (
                    <div key={row.label} className={`grid grid-cols-[1.1fr_1fr_1fr_1.15fr] ${r % 2 ? "bg-white/[0.015]" : ""}`}>
                      <div className="flex items-center px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{row.label}</div>
                      {row.values.map((v, i) => (
                        <div key={i} className={`px-5 py-3.5 text-sm leading-snug ${i === 2 ? "border-x border-gold/30 bg-gold/[0.06] font-medium text-white" : "text-slate-400"}`}>{v}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                What you keep that a franchise takes.
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-8 grid gap-5 sm:grid-cols-2">
                {[
                  ["Your brand", "Operate under your own name and keep the local reputation you built — or co-brand with Agency HQ."],
                  ["Your commission", "Retain up to 95% of what you generate, instead of paying franchise fees on every sale."],
                  ["Your independence", "No establishment costs and no rulebook — just a low monthly administration membership."],
                  ["Your support", "A full operations team behind you: contracts, trust accounting, compliance, marketing and more."],
                ].map(([t, d]) => (
                  <li key={t} className="rounded-2xl border border-white/10 bg-panel/60 p-6">
                    <p className="font-display text-lg font-bold text-white">{t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 rounded-2xl border border-gold/40 bg-gold/[0.06] p-7 text-center">
                <p className="font-display text-xl font-bold text-white">Ready to compare numbers?</p>
                <p className="mt-2 text-sm text-slate-400">Confidential, no obligation — and never shared with your current agency.</p>
                <a href="/#contact" onClick={go("contact", "seo-franchise")} className="mt-5 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25">
                  Book a confidential conversation
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCTA />
    </>
  );
};

export default FranchiseAlternative;
